import React, { useState, useEffect, useCallback } from 'react';
import { Slider } from '@mui/material';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';
import Box from '@mui/material/Box';
import { format, addHours } from 'date-fns';
import './slider_analy_crossborder.css';

const TimeSliderCrossborder = ({ initialDate, steps, onChange, fechaMinSlider }) => {
  const [sliderValue, setSliderValue] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [adjustedDateTime, setAdjustedDateTime] = useState('');
  const [sliderThumbPosition, setSliderThumbPosition] = useState({ top: 0, left: 0 });
  const [isAlertVisible, setIsAlertVisible] = useState(true);

  // Inicializamos startDate a partir de initialDate (fechaMinSlider)
  const [startDate, setStartDate] = useState(() => new Date(initialDate));

  // Generamos las marcas: de 07 a 21 si steps es 15
  const marks = Array.from({ length: steps }, (_, i) => ({
    value: i,
    label: (7 + i).toString()
  }));

  const handlePlayButtonClick = () => {
    setIsAutoPlay(!isAutoPlay);
    setIsPlaying(!isPlaying);
    setIsAlertVisible(true);
  };

  const debounce = (func, wait) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  };

  const debouncedOnChange = useCallback(
    debounce((selectedDate) => {
      onChange(selectedDate);
    }, 500),
    [onChange]
  );

  const handleSliderChange = (event, value) => {
    setSliderValue(value);
    const selectedDate = addHours(startDate, value);
    debouncedOnChange(selectedDate);
    setAdjustedDateTime(format(selectedDate, 'yyyy-MM-dd HH:mm:ss'));
    setIsAlertVisible(true);
  };

  useEffect(() => {
    const sliderThumb = document.querySelector('.horizontal-slider .thumb');
    if (sliderThumb) {
      const sliderRect = sliderThumb.getBoundingClientRect();
      setSliderThumbPosition({
        top: sliderRect.top - 500,
        left: sliderRect.left + sliderRect.width / 2,
      });
    }
  }, [sliderValue]);

  useEffect(() => {
    if (isAutoPlay) {
      const interval = setInterval(() => {
        setSliderValue((prevValue) => {
          const nextValue = prevValue < steps - 1 ? prevValue + 1 : 0;
          const newDateTime = addHours(startDate, nextValue);
          debouncedOnChange(newDateTime);
          setAdjustedDateTime(format(newDateTime, 'yyyy-MM-dd HH:mm:ss'));
          return nextValue;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlay, startDate, steps, debouncedOnChange]);

  // Si cambia la prop fechaMinSlider, actualizamos la base y reiniciamos el slider
  useEffect(() => {
    const nuevaFecha = new Date(fechaMinSlider);
    setStartDate(nuevaFecha);
    setSliderValue(0);
  }, [fechaMinSlider]);

  // Sincronización inicial con el padre
  useEffect(() => {
    if (fechaMinSlider) {
      const initialSelectedDate = addHours(startDate, sliderValue);
      setAdjustedDateTime(format(initialSelectedDate, 'yyyy-MM-dd HH:mm:ss'));
      onChange(initialSelectedDate);
    }
  }, [fechaMinSlider, sliderValue, onChange, startDate]);

  return (
    <div className="slider-container_perf">
      {isAutoPlay ? (
        <button className="custom-btn stop-btn" onClick={handlePlayButtonClick}>
          <StopCircleRoundedIcon style={{ fontSize: 28 }} />
        </button>
      ) : (
        <button className="custom-btn play-btn" onClick={handlePlayButtonClick}>
          <PlayCircleOutlineRoundedIcon style={{ fontSize: 28 }} />
        </button>
      )}
      <Box className="div-slider">
        <Slider
          value={sliderValue}
          step={1}
          marks={marks}
          min={0}
          max={steps - 1}
          onChange={handleSliderChange}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) =>
            format(addHours(startDate, value), "yyyy-MM-dd HH:mm:ss 'UTC'")
          }
          sx={{
            color: 'primary.main',
            height: 8,
            marginLeft: 8,
            width: '92%',
            '& .MuiSlider-thumb': {
              height: 14,
              width: 14,
              marginTop: '1.25%',
              backgroundColor: '#fff',
              '&:focus, &:hover, &.Mui-active': {
                boxShadow: '0px 0px 8px 2px rgba(0,0,0,0.3)',
                height: 18,
                width: 18,
              },
            },
            '& .MuiSlider-track': {
              height: 4,
              marginTop: '1.25%',
              color: '#3498db',
              '&:active': { color: '#2980b9' },
            },
            '& .MuiSlider-rail': {
              color: '#d0e7ff',
              opacity: 1,
              height: 8,
              width: '100%',
              marginTop: '1.25%',
            },
            '& .MuiSlider-valueLabel': {
              position: 'absolute',
              fontFamily: 'Arial',
              backgroundColor: 'YELLOW',
              color: 'BLACK',
              transform: 'translateY(-120%) scale(1)',
              borderRadius: '15px',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0px 4px 12px rgba(0, 128, 0, 0.3)',
              marginTop: '7.5px',
            },
            '& .MuiSlider-mark': {
              backgroundColor: 'transparent',
              height: '8.5px',
              width: '2.5px',
              marginTop: '1.25%',
              '&::after': {
                content: '""',
                display: 'block',
                backgroundColor: '#000',
                height: '9.5px',
                width: '1.25px',
                borderRadius: 0,
              },
            },
            '& .MuiSlider-markLabel': {
              position: 'absolute',
              color: 'gray',
              fontSize: '0.75rem',
              marginTop: '1.05%',
            },
            '& .MuiSlider-markActive': {
              opacity: 0.9,
              backgroundColor: 'currentcolor',
              '&::after': {
                content: '""',
                display: 'block',
                backgroundColor: '#000',
                height: '9.5px',
                width: '2px',
                borderRadius: 0,
                marginTop: '1.25%',
              },
            },
          }}
        />
      </Box>
    </div>
  );
};

export default TimeSliderCrossborder;
